import { t } from 'i18n'
import { useAtom } from 'jotai'
import { lazy, ReactNode, useMemo } from 'react'
import { matchPath, Navigate, useLocation } from 'react-router-dom'
import { shouldDisableNFTRoutesAtom } from 'state/application/atoms'
import { isBrowserRouterEnabled } from 'utils/env'


// High-traffic pages (index and /swap) should not be lazy-loaded.
import Swap from './Swap'


const NotFound = lazy(() => import('pages/NotFound'))


interface RouterConfig {
  browserRouterEnabled?: boolean
  hash?: string
  shouldDisableNFTRoutes?: boolean
}

/**
 * Convenience hook which organizes the router configuration into a single object.
 */
export function useRouterConfig(): RouterConfig {
  const browserRouterEnabled = isBrowserRouterEnabled()
  const { hash } = useLocation()
  const [shouldDisableNFTRoutes] = useAtom(shouldDisableNFTRoutesAtom)
  return useMemo(
    () => ({
      browserRouterEnabled,
      hash,
      shouldDisableNFTRoutes: Boolean(shouldDisableNFTRoutes),
    }),
    [browserRouterEnabled, hash, shouldDisableNFTRoutes]
  )
}

// SEO titles and descriptions sourced from https://docs.google.com/spreadsheets/d/1_6vSxGgmsx6QGEZ4mdHppv1VkuiJEro3Y_IopxUHGB4/edit#gid=0
// getTitle and getDescription are used as static metatags for SEO. Dynamic metatags should be set in the page component itself
const StaticTitlesAndDescriptions = {
  AppTitle: t`Nuts.family dApp | Burn NUT tokens to mint NUTS NFTs`,
  AppDescription: t`Burn NUT tokens to mint NUTS NFTs on Ethereum thanks to Nuts.family dApp`,
  WrapTitle: t`Nuts.family dApp | Wrap NUTS NFTS to NUTS404 ERC20 tokens`,
  WrapDescription: t`Wrap NUTS NFTS to NUTS404 ERC20 tokens on Ethereum thanks to Nuts.family dApp`,
}

export interface RouteDefinition {
  path: string
  nestedPaths: string[]
  getTitle: (path?: string) => string
  getDescription: (path?: string) => string
  enabled: (args: RouterConfig) => boolean
  getElement: (args: RouterConfig) => ReactNode
}

// Assigns the defaults to the route definition.
function createRouteDefinition(route: Partial<RouteDefinition>): RouteDefinition {
  return {
    getElement: () => null,
    getTitle: () => StaticTitlesAndDescriptions.AppTitle,
    getDescription: () => StaticTitlesAndDescriptions.AppDescription,
    enabled: () => true,
    path: '/',
    nestedPaths: [],
    // overwrite the defaults
    ...route,
  }
}

export const routes: RouteDefinition[] = [
  createRouteDefinition({
    path: '/',
    getTitle: () => StaticTitlesAndDescriptions.AppTitle,
    getDescription: () => StaticTitlesAndDescriptions.AppDescription,
    getElement: () => <Swap />,
    
  }),
  createRouteDefinition({
    path: '/mint',
    getTitle: () => StaticTitlesAndDescriptions.AppTitle,
    getDescription: () => StaticTitlesAndDescriptions.AppDescription,
    getElement: () => <Swap />,
    
  }),
  createRouteDefinition({
    path: '/wrap',
    getTitle: () => StaticTitlesAndDescriptions.WrapTitle,
    getDescription: () => StaticTitlesAndDescriptions.WrapDescription,
    getElement: () => <Swap />,
    
  }),
  createRouteDefinition({ path: '*', getElement: () => <Navigate to="/not-found" replace /> }),
  createRouteDefinition({ path: '/not-found', getElement: () => <NotFound /> }),
]

export const findRouteByPath = (pathname: string) => {
  for (const route of routes) {
    const match = matchPath(route.path, pathname)
    if (match) {
      return route
    }
    const subPaths = route.nestedPaths.map((nestedPath) => `${route.path}/${nestedPath}`)
    for (const subPath of subPaths) {
      const match = matchPath(subPath, pathname)
      if (match) {
        return route
      }
    }
  }
  return undefined
}
